/**
 * Desktop sidebar widget catalog. Each widget appears in the right rail
 * on web (>=1024px viewport) when its key is present in the user's
 * `sidebar_widgets_json` array. NULL on the property → defaults below.
 *
 * Adding a new widget:
 *   1. Add a key + label entry here.
 *   2. Add a render case in RightRail.tsx.
 *   3. (Optional) bump DEFAULT_WIDGETS if it should appear out of the box.
 */
export interface SidebarWidget {
  key: string;
  label: string;
  sub: string;
}

export const WIDGETS: SidebarWidget[] = [
  {
    key: 'nickname',
    label: 'Home nickname',
    sub: 'Compact card showing the house name + address.',
  },
  {
    key: 'map',
    label: 'Map preview',
    sub: 'Static map of your address — same one as the My Home page.',
  },
  {
    key: 'upcoming_tasks',
    label: 'Upcoming tasks',
    sub: 'Next few items on the plan so you can see what’s ahead at a glance.',
  },
  {
    key: 'past_tasks',
    label: 'Past tasks',
    sub: 'Last few things you knocked out, color-coded by done vs skipped.',
  },
  {
    key: 'systems',
    label: 'Systems snapshot',
    sub: 'Quick list of your tracked systems with reminder counts.',
  },
  {
    key: 'house_details',
    label: 'House details',
    sub: 'Bedrooms, bathrooms, sqft, HVAC, lot size — the basics at a glance.',
  },
  {
    key: 'recent_activity',
    label: 'Recent activity',
    sub: 'Latest moves — tasks done, notes jotted, expenses logged. Tap to drill in.',
  },
  {
    key: 'weather',
    label: 'Weather',
    sub: 'Today + frost watch + forecast — same panel as the home screen.',
  },
  {
    key: 'projects',
    label: 'Projects',
    sub: "What you're building toward — your active projects, one tap away.",
  },
];

/** Out-of-the-box selection — nickname, map, upcoming, past in that order. */
export const DEFAULT_WIDGETS: string[] = ['nickname', 'map', 'upcoming_tasks', 'past_tasks'];

export function parseWidgetKeys(raw: string | null | undefined): string[] {
  if (raw == null) return DEFAULT_WIDGETS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_WIDGETS;
    const valid = new Set(WIDGETS.map((w) => w.key));
    // Preserve the user-chosen order — the saved array IS the order in
    // which widgets stack in the sidebar. Filter to valid keys (drops
    // anything from older app versions or accidental writes). Empty
    // saved list = an intentional "show nothing" — respect it.
    return (arr as unknown[])
      .filter((k): k is string => typeof k === 'string' && valid.has(k));
  } catch {
    return DEFAULT_WIDGETS;
  }
}
